<template>
  <div>
    <b-container class="pt-4">
      <b-row>
        <b-col lg="6">
          <BDXMatchPendingManager />
        </b-col>
        <b-col lg="6">
          <BDXStatusPendingManager />
        </b-col>
      </b-row>
    </b-container>

    <BDXManagementTable
      ref="tableRef"
      v-model="selectedIds"
      :selected="selectedIds"
      @edit:item="(item) => setActiveBDX(item)"
      @transactions:item="(item) => openChargeTransactions(item)"
    >
      <template v-slot:header(bottom)>
        <BaseButton
          v-if="!!selectedIds.length"
          class="ml-3"
          @click="handleEditClick"
          >Edit</BaseButton
        >
        <BaseButton
          v-if="!!selectedIds.length && isSuperUser"
          class="ml-2"
          @click="showBulkInvoiceChargeModal = true"
          >Create Invoice Charges</BaseButton
        >
      </template>
    </BDXManagementTable>

    <BDXBulkEditForm
      v-model="showBulkEditModal"
      :ids="selectedIds"
      is-modal
      title="BDX Bulk Edit"
    />

    <ChargeTransactionsModal
      v-model="showChargeTransactionsModal"
      :bdx-key="activeChargesBDXKey"
      :currency-type="activeChargesCurrencyType"
      :tpa="activeChargesTPA"
    />

    <BulkInvoiceChargeModal
      v-model="showBulkInvoiceChargeModal"
      :selected-items="selectedItems"
    />

    <BDXEditForm
      v-model="showEditModal"
      :ids="!!selectedIds.length ? selectedIds : [activeBDX.id]"
      :lead-underwriters="activeBDX.lead_underwriter"
      :follower-underwriters="activeBDX.follower_underwriter"
      :tpa="activeBDX.tpa"
      :broker="activeBDX.broker"
      :coverholder="activeBDX.coverholder"
      :ucr="activeBDX.ucr"
      :fcp-status="activeBDX.fcp_status"
      :fcp-start-date="activeBDX.fcp_start_date"
      :currency-type="activeBDX.currency_type"
      :year-of-account="activeBDX.year_of_account"
      :continuous-contract="activeBDX.continuous_contract"
      :account-name="activeBDX.account_name"
      :umr="activeBDX.umr"
      :account-code="activeBDX.account_code"
      :account-type="activeBDX.account_type"
      :jurisdiction="activeBDX.jurisdiction"
      is-modal
      title="BDX Edit"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  watch,
  nextTick,
  onMounted
} from '@vue/composition-api'
import { clone, differenceBy, head, tail, unionBy } from 'lodash-es'

import { useActiveBDX, useUserCompany, useCurrentUser } from '@composables'
import { BaseButton } from '@components/base'
import {
  BDXEditForm,
  BDXMatchPendingManager,
  BDXStatusPendingManager,
  BDXManagementTable,
  BDXBulkEditForm,
  ChargeTransactionsModal,
  BulkInvoiceChargeModal
} from '@components/bdx'

export default defineComponent({
  name: 'ManageBDX',
  components: {
    BaseButton,

    BDXEditForm,
    BDXMatchPendingManager,
    BDXStatusPendingManager,
    BDXManagementTable,
    BDXBulkEditForm,
    ChargeTransactionsModal,
    BulkInvoiceChargeModal,
  },
  setup() {
    const { activeBDX, setActiveBDX, resetActiveBDX } = useActiveBDX()
    const { roleName: companyRoleName } = useUserCompany()
    const { isSuperUser } = useCurrentUser()

    const tableRef = ref(null)

    const excludedWizardSteps = computed(() => {
      if ('TPA' === companyRoleName.value) return ['TPANames']
      else if ('Coverholder' === companyRoleName.value) return ['Coverholders']
      else if ('Broker' === companyRoleName.value) return ['Brokers']
      else return []
    })

    // Selected items
    const selectedIds = ref([])
    const selectedItems = ref([])

    watch(selectedIds, (newSelectedIds) => {
      const { dataTableRef } = tableRef.value || {}
      const { tableRef: dTable } = dataTableRef || {}

      if (!dTable.tableRef) return

      resetActiveBDX()

      if (!!newSelectedIds.length) {
        const mappedItems = newSelectedIds.map((newSelectedId) => {
          const localItem = dTable.tableRef.localItems.find(
            (localItem) => localItem.id === newSelectedId
          )

          return localItem
        })

        selectedItems.value = mappedItems.filter(Boolean)

        if (1 < mappedItems.length) {
          const firstSelectedItem = head(mappedItems)
          const nextSelectedItems = tail(mappedItems)

          const newActiveBDX = clone(firstSelectedItem)

          const fields = [
            'fcp_status',
            'fcp_start_date',
            'year_of_account',
            'currency_type',
            'ucr',
            'account_type',
            'account_name',
            'account_code'
          ]

          fields.forEach((field) => {
            const valuesDiff = differenceBy(
              [firstSelectedItem],
              nextSelectedItems,
              field
            )

            if (!!valuesDiff.length) {
              newActiveBDX[field] = undefined
            }
          })

          mappedItems.forEach((item) => {
            if (
              newActiveBDX.tpa &&
              item.tpa &&
              newActiveBDX.tpa.id !== item.tpa.id
            ) {
              newActiveBDX.tpa = []
            }
            newActiveBDX.underwriter = unionBy(
              newActiveBDX.underwriter,
              item.underwriter,
              'rls_underwriter.id'
            )
            newActiveBDX.broker = unionBy(
              newActiveBDX.broker,
              item.broker,
              'id'
            )
            if (
              newActiveBDX.coverholder &&
              item.coverholder &&
              newActiveBDX.coverholder.id !== item.coverholder.id
            ) {
              newActiveBDX.coverholder = []
            }

            if (1 < newActiveBDX.tpa) {
              newActiveBDX.tpa = []
            }
          })

          newActiveBDX.continuous_contract = mappedItems.every(({continuous_contract}) => {
            return continuous_contract;
          });

          setActiveBDX(newActiveBDX)
        } else {
          setActiveBDX(mappedItems[0])
        }
      } else {
        selectedItems.value = []
      }
    })

    // Handle Edit button click
    const handleEditClick = () => {
      if (selectedIds.value.length === 1) {
        showEditModal.value = true
      } else {
        showBulkEditModal.value = true
      }
    }

    //Bulk Edit Modal 
    const showBulkEditModal = ref(false);
    watch(showBulkEditModal, (newShowEditModal) => {
      if (newShowEditModal) return

      selectedIds.value = []
    })

    // Edit modal
    const showEditModal = ref(false)

    watch(showEditModal, (newShowEditModal) => {
      if (newShowEditModal) return

      selectedIds.value = []
    })

    watch(
      activeBDX,
      (newActiveBDX) => {
        if (!!selectedIds.value.length) return

        showEditModal.value = !!newActiveBDX.id
      },
      { deep: true }
    )

    onMounted(() => {
      Vue.prototype.$emitter.on('bdx-edit-form:success', async (status) => {
        if (!status) return

        await nextTick()

        if (!!tableRef.value) await tableRef.value.dataTableRef.handleRefresh()

        selectedIds.value = []
      })

      Vue.prototype.$emitter.on('invoice-charge:success', async () => {
        await nextTick()
        if (!!tableRef.value) await tableRef.value.dataTableRef.handleRefresh()
      })
    })

    // Charge Transactions Modal
    const showChargeTransactionsModal = ref(false)
    const activeChargesBDXKey = ref('')
    const activeChargesCurrencyType = ref('')
    const activeChargesTPA = ref('')

    const openChargeTransactions = (item) => {
      activeChargesBDXKey.value = item.umr_rc_cc_sn || ''
      activeChargesCurrencyType.value = item.currency_type || ''
      activeChargesTPA.value = (item.tpa && item.tpa.TPA) || ''
      showChargeTransactionsModal.value = true
    }

    // Bulk Invoice Charge Modal
    const showBulkInvoiceChargeModal = ref(false)

    return {
      activeBDX,
      setActiveBDX,

      tableRef,

      excludedWizardSteps,

      selectedIds,
      selectedItems,

      isSuperUser,

      handleEditClick,

      showEditModal,
      showBulkEditModal,

      showChargeTransactionsModal,
      activeChargesBDXKey,
      activeChargesCurrencyType,
      activeChargesTPA,
      openChargeTransactions,

      showBulkInvoiceChargeModal,
    }
  }
})
</script>

<style scoped>
.custom-checkbox >>> .custom-control-label {
  padding-left: 20px;
  margin-bottom: 0;
}
</style>
